// frontend/src/utils/planningCellParsers.js
//
// AG Grid `valueParser`s for the planning cells the SaaS grids edit — one home
// for the rules Inventory, Item setup, the forecast grid and Supplier setup
// share.
//
// Pure functions (they take the locale-aware `parseNumber` from `useFormatters`
// rather than reaching for it) so the wire contract can be pinned by unit tests
// without rendering a grid — same reason itemSetupRow.js exists. A parser that
// reads numbers without `parseNumber` misreads a comma-decimal merchant's
// "12,5" as 125.
//
// ── The two rules these encode ──────────────────────────────────────────────
//
// NOTHING IS CLAMPED. A clamp answers a merchant who typed −5 with a saved 0 —
// a plausible number they never chose, in a column they will later plan
// against. The server states each bound once (`INVENTORY_FIELD_SPECS`,
// backend/core/validators.py; `final_units_field`,
// backend/forecasting/consensus.py) and refuses the row with a sentence
// naming the field and the range.
//
// A BLANK MEANS WHAT THE COLUMN SAYS IT MEANS. A null `safety_stock_units` is a
// real saveable intent — "clear my override" — which the server resolves back
// to the engine's figure (backend/api/services/safety_stock.py); the column
// itself is NOT NULL and never holds the blank. So is an empty forecast month,
// which restores the statistical value the same way. `InventoryOnHand` is not
// nullable and has no such reading: an empty box there leaves the stored
// quantity alone rather than zeroing the merchant's stock. `min_inventory_units`
// is not nullable either, but its zero IS "none": an empty Minimum stock box
// says the merchant keeps no minimum, so it parses as 0. Neither column ever
// sends a blank: the server refuses one on a non-nullable field
// (`parse_grid_field`, backend/core/validators.py).

import { roundServiceLevel, serviceLevelInRange } from './serviceLevel';

/**
 * Service level: refuse out of range, round extra precision.
 *
 * OUT OF RANGE IS REFUSED, not clamped, because the bound is not what the
 * merchant meant: someone typing 150 meant 95 or 15, and storing 99.99 for them
 * hands back a number they never chose. The cell simply does not take the value
 * — the same treatment it gives an unparseable entry — and the column's header
 * tooltip names the range.
 *
 * EXTRA PRECISION IS ROUNDED, because 95.456 does mean 95.46 at the two decimal
 * places the column stores; that is the same number, not a different one.
 *
 * ROUNDING HAPPENS FIRST: 99.995 is "< 100" as typed but rounds to 100.00,
 * which the serializer and `item_location_service_level_pct_range` both
 * reject.
 *
 * A BLANK KEEPS THE STORED VALUE: the column is NOT NULL, so there is no
 * "clear" to express. That differs from decimalColumn's own parser, which
 * returns null — correct for a nullable column, an IntegrityError here.
 */
export function parseServiceLevelCell({ newValue, oldValue }, parseNumber) {
  if (newValue === '' || newValue == null) return oldValue;
  const parsed = parseNumber(newValue);
  if (isNaN(parsed)) return oldValue;
  const rounded = roundServiceLevel(parsed);
  if (!serviceLevelInRange(rounded)) return oldValue;
  return rounded;
}

/**
 * A whole-unit quantity whose column is nullable, where a blank CLEARS it.
 *
 * `null` is the saveable "no override — fall back to the system value", which is
 * why a blank does NOT fall back to `oldValue` the way `parseWholeUnitsCell`
 * does. Used by the safety-stock columns on Inventory and Item setup, by the
 * forecast month columns, where a blank restores the statistical value, and by
 * the Supplier setup planning overrides (pages/supplierPlanningColumns.js),
 * where a blank returns the row to the store's value.
 */
export function parseClearableUnitsCell({ newValue, oldValue }, parseNumber) {
  if (newValue === '' || newValue == null) return null;
  const parsed = Math.round(parseNumber(newValue));
  if (isNaN(parsed)) return oldValue;
  return parsed;
}

/**
 * A whole-unit quantity whose column is NOT NULL and whose zero means "none",
 * so a blank IS that zero.
 *
 * Used by the Minimum stock column (utils/minimumStockColDef.js): clearing the
 * box removes the minimum. The 0 is what goes on the wire — the server refuses
 * a blank for this field (`parse_grid_field`, backend/core/validators.py).
 */
export function parseBlankIsZeroUnitsCell({ newValue, oldValue }, parseNumber) {
  if (newValue === '' || newValue == null) return 0;
  const parsed = Math.round(parseNumber(newValue));
  if (isNaN(parsed)) return oldValue;
  return parsed;
}

/**
 * A whole-unit quantity whose column is NOT NULL, where a blank means nothing.
 *
 * A cleared box keeps the stored quantity rather than parsing as 0 — see the
 * module header. Used by the on-hand column.
 */
export function parseWholeUnitsCell({ newValue, oldValue }, parseNumber) {
  if (newValue === '' || newValue == null) return oldValue;
  const parsed = Math.round(parseNumber(newValue));
  if (isNaN(parsed)) return oldValue;
  return parsed;
}
